import { message, Table, TablePaginationConfig, Tooltip } from 'antd';
import { ColumnsType, SorterResult } from 'antd/lib/table/interface';
import { toString } from 'lodash';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { QualityContainer } from '~/components/atoms/QualityContainer';
import { Flex } from '~/components/bosons/FlexBox.styled';
import type { IColumnGroup } from '~/components/molecules/Shared/ColumnVisibilityDropdown/ColumnVisibilityDropdown';
import ColumnVisibilityDropdown from '~/components/molecules/Shared/ColumnVisibilityDropdown/ColumnVisibilityDropdown';
import ErrorMessage from '~/components/molecules/Shared/ErrorMessage';
import { QualityColorEnum } from '~/constants/QualityColor.enum';
import { SaleStatement } from '~/domain/api';
import { SaleStatementTypeEnum } from '~/domain/enum/salesStatement/saleStatementType.enum';
import { currencyFormatter } from '~/helpers/util/number';
import { SorterEnum } from '~/zustand/productRegistrationRequest/enums';

import { TableContainer } from '~/components/bosons/TableContainer.styled';
import useSaleStatementByTypeStore, {
  defaultColumnOrderBy,
} from '~/zustand/salesStatement/useSalesStatementByTypeStore';
import * as S from './SalesStatementIntermediationTable.styled';

const rowKey = (row: SaleStatement) => row.uniqueId;

const totalValueTitleWithTooltip = () => {
  return (
    <>
      <Tooltip
        placement="bottomLeft"
        title={
          <>
            <strong>Cálculo:</strong> Valor Unitário × Quantidade Total, sem custo de embalagem e
            sem custo de frete
          </>
        }
      >
        <S.QuestionMark />
      </Tooltip>
      Valor Total
    </>
  );
};

const COLUMN_GROUPS: IColumnGroup[] = [
  {
    label: 'Identificação',
    columns: [
      { key: 'salesChannel', label: 'Origem' },
      { key: 'orderNumber', label: 'Nº Pedido' },
      { key: 'siteCode', label: 'Chave' },
      { key: 'customerDescription', label: 'Cliente' },
    ],
  },
  {
    label: 'Datas',
    columns: [
      { key: 'transactionDate', label: 'Data do Pedido' },
      { key: 'billingDate', label: 'Data de Entrega' },
      { key: 'commercialDate', label: 'Data Comercial' },
    ],
  },
  {
    label: 'Produto',
    columns: [
      { key: 'productDescription', label: 'Produto' },
      { key: 'qualityDescription', label: 'QLD.' },
      { key: 'packingCode', label: 'Embalagem' },
      { key: 'quantity', label: 'QE x QpE' },
    ],
  },
  {
    label: 'Valores',
    columns: [
      { key: 'totalQuantity', label: 'Quant. Total' },
      { key: 'unitValue', label: 'Valor Unit.' },
      { key: 'packingValue', label: 'Valor Emb.' },
      { key: 'freightValue', label: 'Valor Frete' },
      { key: 'totalValue', label: 'Valor Total' },
    ],
  },
];

export const tableColumns: ColumnsType<SaleStatement> = [
  {
    key: 'salesChannel',
    dataIndex: 'salesChannel',
    title: 'Origem',
    align: 'left',
    width: '120px',
    render: (salesChannel: string) => <S.Text>{salesChannel}</S.Text>,
  },
  {
    key: 'transactionDate',
    dataIndex: 'transactionDate',
    title: 'Data do Pedido',
    align: 'center',
    sorter: true,
    width: '120px',
    render: (transactionDate: string) => <S.Text>{transactionDate}</S.Text>,
  },
  {
    key: 'billingDate',
    dataIndex: 'billingDate',
    title: 'Data de Entrega',
    align: 'center',
    sorter: true,
    width: '120px',
    render: (deliveryDate: string) => <S.Text>{deliveryDate}</S.Text>,
  },
  {
    key: 'commercialDate',
    dataIndex: 'commercialDate',
    title: 'Data Comercial',
    align: 'center',
    sorter: true,
    width: '120px',
    render: (commercialDate: string) => <S.Text>{commercialDate}</S.Text>,
  },
  {
    key: 'orderNumber',
    dataIndex: 'orderNumber',
    title: 'Nº Pedido',
    align: 'center',
    width: '120px',
    render: (orderNumber: string) => <S.Text>{orderNumber}</S.Text>,
  },
  {
    key: 'siteCode',
    dataIndex: 'siteCode',
    title: 'Chave',
    align: 'left',
    sorter: true,
    width: '120px',
    render: (siteCode: string) => <S.Text>{siteCode}</S.Text>,
  },
  {
    key: 'customerDescription',
    dataIndex: 'customerDescription',
    title: 'Cliente',
    align: 'left',
    sorter: true,
    width: '180px',
    render: (customerDescription: string) => <S.Text>{customerDescription}</S.Text>,
  },
  {
    key: 'productDescription',
    title: 'Produto',
    dataIndex: 'productDescription',
    width: '180px',
    align: 'left',
    sorter: true,
    render: (_: string, saleStatement: SaleStatement) => (
      <>
        <Tooltip title={saleStatement.productLongDescription}>
          <S.Text>{`${saleStatement.productLongDescription}`}</S.Text>
        </Tooltip>
        <S.GrayText>{`${saleStatement.productCode}`}</S.GrayText>
      </>
    ),
  },
  {
    key: 'qualityDescription',
    title: 'QLD.',
    dataIndex: 'qualityDescription',
    width: '60px',
    align: 'center',
    render: (qualityDescription: string) => {
      return (
        <QualityContainer quality={qualityDescription as QualityColorEnum}>
          {qualityDescription}
        </QualityContainer>
      );
    },
  },
  {
    key: 'packingCode',
    title: 'Embalagem',
    dataIndex: 'packingCode',
    width: '120px',
    align: 'left',
    render: (_: string, saleStatement: SaleStatement) => (
      <>
        <Tooltip title={saleStatement.packingCode}>
          <S.Text>{`${saleStatement.packingCode}`}</S.Text>
        </Tooltip>
        <S.GrayText>{`${saleStatement.packingDescription}`}</S.GrayText>
      </>
    ),
  },
  {
    key: 'quantity',
    dataIndex: 'quantity',
    title: 'QE x QpE',
    width: '80px',
    align: 'center',
    render: (quantity: string) => <S.Text>{quantity}</S.Text>,
  },
  {
    key: 'totalQuantity',
    dataIndex: 'totalQuantity',
    title: 'Quant. Total',
    align: 'left',
    sorter: true,
    width: '80px',
    render: (totalQuantity: number) => <S.Text>{totalQuantity}</S.Text>,
  },
  {
    key: 'unitValue',
    title: 'Valor Unit.',
    dataIndex: 'unitValue',
    width: '100px',
    align: 'right',
    sorter: true,
    render: (unitValue: number) => {
      return unitValue ? currencyFormatter(unitValue, true) : '--';
    },
  },
  {
    key: 'packingValue',
    title: 'Valor Emb.',
    dataIndex: 'packingValue',
    width: '100px',
    align: 'right',
    sorter: true,
    render: (packingValue: number) => {
      return packingValue ? currencyFormatter(packingValue, true) : '--';
    },
  },
  {
    key: 'freightValue',
    title: 'Valor Frete',
    dataIndex: 'freightValue',
    width: '100px',
    align: 'right',
    sorter: true,
    render: (freightValue: number) => {
      return freightValue ? `R$ ${freightValue}` : '--';
    },
  },
  {
    key: 'totalValue',
    title: totalValueTitleWithTooltip(),
    dataIndex: 'totalValue',
    width: '100px',
    align: 'right',
    sorter: true,
    render: (totalValue: number) => (
      <strong>{totalValue ? currencyFormatter(totalValue, true) : '--'}</strong>
    ),
  },
];

export default function SalesStatementIntermediationTable() {
  const {
    isError,
    isLoading,
    dataSource,
    tableParams,
    fetchTable,
    setDataStore,
    formFilterInstance,
    visibleKeys,
    toggleColumnVisibility,
    resetColumnVisibility,
  } = useSaleStatementByTypeStore(
    useShallow(state => ({
      isError: state.intermediationTable.isError,
      isLoading: state.intermediationTable.isLoading,
      dataSource: state.intermediationTable.data,
      tableParams: state.intermediationTable.tableParams,
      fetchTable: state.handleIntermediationTable,
      setDataStore: state.setData,
      formFilterInstance: state.intermediationFormFilterInstance,
      visibleKeys: state.intermediationVisibleColumnKeys,
      toggleColumnVisibility: state.toggleColumnVisibility,
      resetColumnVisibility: state.resetColumnVisibility,
    })),
  );

  const columns = tableColumns.filter(c => visibleKeys.includes(c.key as string));

  const handleTableChange = useCallback(
    async (pagination: TablePaginationConfig, sorter?: SorterResult<SaleStatement>) => {
      try {
        await formFilterInstance?.validateFields?.();

        const sortField = !sorter?.order
          ? defaultColumnOrderBy
          : toString(sorter?.field) || defaultColumnOrderBy;

        const sort = sorter?.order === 'descend' ? SorterEnum.DESC : SorterEnum.ASC;

        setDataStore(state => {
          state.intermediationTable.tableParams.pagination.current = pagination.current;
          state.intermediationTable.tableParams.pagination.pageSize = pagination.pageSize;
          state.intermediationTable.tableParams.orderBy = sortField;
          state.intermediationTable.tableParams.sort = sort;
          state.intermediationTable.data = [];
        });

        fetchTable();
      } catch (error) {
        message.error('Erro ao buscar os dados');
      }
    },
    [formFilterInstance, setDataStore, fetchTable],
  );

  if (isError) {
    return (
      <ErrorMessage message="Ocorreu um erro inesperado! Não foi possível trazer os dados da tabela. Atualize a página e tente novamente." />
    );
  }

  return (
    <TableContainer data-testid="sales-statement-table">
      <Flex $justify="flex-end" style={{ marginBottom: 8 }}>
        <ColumnVisibilityDropdown
          groups={COLUMN_GROUPS}
          visibleKeys={visibleKeys}
          onToggle={key => toggleColumnVisibility(SaleStatementTypeEnum.Intermediation, key)}
          onReset={() => resetColumnVisibility(SaleStatementTypeEnum.Intermediation)}
        />
      </Flex>
      <Table
        columns={columns}
        sortDirections={['ascend', 'descend']}
        rowKey={rowKey}
        loading={isLoading}
        pagination={tableParams.pagination}
        dataSource={dataSource}
        onChange={(page: TablePaginationConfig, _, sorter) => {
          handleTableChange(page, sorter as SorterResult<SaleStatement>);
        }}
        scroll={{ y: 470 }}
      />
    </TableContainer>
  );
}
